// API Configuration
import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
export const WS_URL = API_URL.replace(/^http/, 'ws') + '/ws';

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  login: `${API_URL}/api/auth/login`,
  demoLogin: `${API_URL}/api/user/demo/login`,
  logout: `${API_URL}/api/auth/logout`,
  refresh: `${API_URL}/api/auth/refresh`,
  
  // Eligibility
  checkEligibility: `${API_URL}/api/eligibility`,
  
  // User Selections
  userSelections: `${API_URL}/api/user-selections`,
  
  // Dashboard
  dashboard: (facilityId: string) => `${API_URL}/api/dashboard/${facilityId}`,
  
  // Transactions
  transactions: `${API_URL}/api/transactions`,
  transactionsByFacility: (facilityId: string) => `${API_URL}/api/transactions/facility/${facilityId}`,
  exerciseNotice: `${API_URL}/api/transactions/exercise-notice`,
  
  // Term Sheet
  termSheetDownload: (selectionId: string) => `${API_URL}/api/term-sheet/download/${selectionId}`,
  
  // Questionnaire
  questionnairePrepare: (facilityId: string) => `${API_URL}/api/questionnaire/prepare/${facilityId}`,
  questionnaireSubmit: `${API_URL}/api/questionnaire/submit`,
  
  // Notifications
  notifications: `${API_URL}/api/notifications`,
  notificationRead: (notificationId: string) => `${API_URL}/api/notifications/${notificationId}/read`,
  
  // Settlement
  settlementTradeFileUpload: `${API_URL}/api/settlement/trade-file/upload`,
  settlementPNL: (transactionId: string) => `${API_URL}/api/settlement/pnl/${transactionId}`,
  settlementGenerate: (transactionId: string) => `${API_URL}/api/settlement/generate/${transactionId}`,
};

// Add request interceptor to include auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't retry if this is already a retry attempt
    if (originalRequest._retry) {
      // If this is a retry that failed, check if it's a long-running operation
      const isLongRunningOperation = originalRequest.url?.includes('/enhanced-s1') || 
                                    originalRequest.url?.includes('/s1-generation') ||
                                    originalRequest.url?.includes('/generate');
      
      if (isLongRunningOperation) {
        // For long-running operations, don't immediately logout
        // Instead, show a more user-friendly error
        console.warn('Long-running operation failed due to authentication timeout');
        const customError = new Error('Operation timed out due to authentication. Please try again.');
        (customError as any).isLongRunningOperation = true;
        return Promise.reject(customError);
      }
      
      // For other operations, proceed with normal logout
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(API_ENDPOINTS.refresh, { refreshToken });
          const { token } = response.data;
          localStorage.setItem('token', token);
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Check if this is a long-running operation
        const isLongRunningOperation = originalRequest.url?.includes('/enhanced-s1') || 
                                      originalRequest.url?.includes('/s1-generation') ||
                                      originalRequest.url?.includes('/generate');
        
        if (isLongRunningOperation) {
          // For long-running operations, don't immediately logout
          console.warn('Long-running operation failed due to authentication timeout');
          const customError = new Error('Operation timed out due to authentication. Please try again.');
          (customError as any).isLongRunningOperation = true;
          return Promise.reject(customError);
        }
        
        // For other operations, proceed with normal logout
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Export the configured axios instance
export default axios; 